import { useState } from 'react'
import { Trash2, FolderOpen, Save, Edit2, Check, X, Download, Upload } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { NotificationDialog, NotificationState, initialNotificationState } from '@/components/ui/notification-dialog'
import { useSavedDesignsStore, SavedDesign, ExportedDesign, ExportedDesignsCollection } from '@/stores/savedDesignsStore'
import { useElementsStore } from '@/stores/elementsStore'
import { useLabelStore } from '@/stores/labelStore'

interface DesignsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function DesignsDialog({ open, onOpenChange }: DesignsDialogProps) {
  const [newDesignName, setNewDesignName] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')
  const [notification, setNotification] = useState<NotificationState>(initialNotificationState)

  const { designs, saveDesign, updateDesign, renameDesign, deleteDesign, currentDesignId, setCurrentDesignId, exportDesign, exportAllDesigns, importDesign } = useSavedDesignsStore()
  const { elements, setElements } = useElementsStore()
  const { label, setLabel } = useLabelStore()

  const handleSaveNew = () => {
    if (!newDesignName.trim()) return
    saveDesign(newDesignName.trim(), elements, label)
    setNewDesignName('')
  }

  const handleSaveCurrent = () => {
    if (currentDesignId) {
      updateDesign(currentDesignId, elements, label)
    }
  }

  const handleLoad = (design: SavedDesign) => {
    setElements(design.elements)
    setLabel(design.labelConfig)
    setCurrentDesignId(design.id)
    onOpenChange(false)
  }

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this design?')) {
      deleteDesign(id)
    }
  }

  const handleStartRename = (design: SavedDesign) => {
    setEditingId(design.id)
    setEditingName(design.name)
  }

  const handleConfirmRename = () => {
    if (editingId && editingName.trim()) {
      renameDesign(editingId, editingName.trim())
    }
    setEditingId(null)
    setEditingName('')
  }

  const handleCancelRename = () => {
    setEditingId(null)
    setEditingName('')
  }

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString()
  }

  const handleExportSingle = (design: SavedDesign) => {
    const data = exportDesign(design.id)
    if (!data) return
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${design.name.replace(/\s+/g, '_')}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const handleExportAll = () => {
    const data = exportAllDesigns()
    if (data.designs.length === 0) {
      setNotification({
        open: true,
        title: 'No Designs',
        description: 'No saved designs to export.',
        variant: 'warning',
      })
      return
    }
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `neo-zpl-designs-${new Date().toISOString().split('T')[0]}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const handleImport = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.json'
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (!file) return

      const reader = new FileReader()
      reader.onload = (event) => {
        try {
          const data = JSON.parse(event.target?.result as string) as ExportedDesign | ExportedDesignsCollection
          const result = importDesign(data)
          if (result.success) {
            setNotification({
              open: true,
              title: 'Import Successful',
              description: `Successfully imported ${result.count} design(s).`,
              variant: 'success',
            })
          } else {
            setNotification({
              open: true,
              title: 'Import Failed',
              description: result.error || 'Unknown error occurred.',
              variant: 'error',
            })
          }
        } catch {
          setNotification({
            open: true,
            title: 'Invalid File',
            description: 'Failed to read file. Make sure it is a valid JSON file.',
            variant: 'error',
          })
        }
      }
      reader.readAsText(file)
    }
    input.click()
  }

  const currentDesign = designs.find((d) => d.id === currentDesignId)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Saved Designs</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Save New Design */}
          <div className="flex gap-2">
            <div className="flex-1">
              <Label htmlFor="design-name" className="sr-only">
                Design Name
              </Label>
              <Input
                id="design-name"
                placeholder="Enter design name..."
                value={newDesignName}
                onChange={(e) => setNewDesignName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSaveNew()}
              />
            </div>
            <Button onClick={handleSaveNew} disabled={!newDesignName.trim()}>
              <Save className="h-4 w-4 mr-2" />
              Save New
            </Button>
          </div>

          {/* Update Current Design */}
          {currentDesign && (
            <div className="flex items-center gap-2 p-2 bg-muted rounded-md">
              <span className="text-sm flex-1">
                Current: <strong>{currentDesign.name}</strong>
              </span>
              <Button variant="outline" size="sm" onClick={handleSaveCurrent}>
                <Save className="h-4 w-4 mr-2" />
                Update
              </Button>
            </div>
          )}

          <Separator />

          {/* Saved Designs List */}
          <div>
            <Label className="text-sm font-medium">Saved Designs ({designs.length})</Label>
            <ScrollArea className="h-[300px] mt-2 border rounded-md">
              {designs.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground">
                  No saved designs yet
                </div>
              ) : (
                <div className="divide-y">
                  {designs
                    .sort((a, b) => b.updatedAt - a.updatedAt)
                    .map((design) => (
                      <div
                        key={design.id}
                        className={`p-3 flex items-center gap-2 hover:bg-muted/50 ${
                          design.id === currentDesignId ? 'bg-muted' : ''
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          {editingId === design.id ? (
                            <div className="flex items-center gap-2">
                              <Input
                                value={editingName}
                                onChange={(e) => setEditingName(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleConfirmRename()
                                  if (e.key === 'Escape') handleCancelRename()
                                }}
                                className="h-8"
                                autoFocus
                              />
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={handleConfirmRename}
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={handleCancelRename}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <>
                              <div className="font-medium truncate">{design.name}</div>
                              <div className="text-xs text-muted-foreground">
                                {design.labelConfig.width}×{design.labelConfig.height}mm •{' '}
                                {design.elements.length} elements • Updated{' '}
                                {formatDate(design.updatedAt)}
                              </div>
                            </>
                          )}
                        </div>
                        {editingId !== design.id && (
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleLoad(design)}
                              title="Load design"
                            >
                              <FolderOpen className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleExportSingle(design)}
                              title="Export design"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleStartRename(design)}
                              title="Rename"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive"
                              onClick={() => handleDelete(design.id)}
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </ScrollArea>
          </div>

          <Separator />

          {/* Export/Import Actions */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleImport}>
              <Upload className="h-4 w-4 mr-2" />
              Import
            </Button>
            <Button variant="outline" onClick={handleExportAll} disabled={designs.length === 0}>
              <Download className="h-4 w-4 mr-2" />
              Export All
            </Button>
          </div>
        </div>
      </DialogContent>

      <NotificationDialog
        open={notification.open}
        onOpenChange={(open) => setNotification((prev) => ({ ...prev, open }))}
        title={notification.title}
        description={notification.description}
        variant={notification.variant}
      />
    </Dialog>
  )
}
